import { MapUtils } from '../../utility/map-utils.js';
import { VersionRange } from '../core/version-range.js';
import { DataModel, DataModelBuilder, isSameData, SerializationContext, SerializedData } from '../dto/dto.js';
import { LocalizedString } from './localized-string.js';
import { PhotoReference } from './file-reference/photo-reference.js';

export class LegacyPointOfInterestItem extends DataModel {
  static readonly TYPE_ID: string = 'e8b7f8153d444268b0cca658e9bd264d';
  static readonly SCHEMA_VERSION: number = 2;

  constructor(
    public readonly titles: LocalizedString,
    public readonly captions: LocalizedString,
    public readonly photo: PhotoReference | null,
    public readonly url: string | null,
  ) {
    super();
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, LegacyPointOfInterestItem.SCHEMA_VERSION);
  }

  get $typeId() {
    return LegacyPointOfInterestItem.TYPE_ID;
  }

  static fromMap(context: SerializationContext, map: SerializedData): LegacyPointOfInterestItem {
    return new LegacyPointOfInterestItem(
        context.fromData<LocalizedString>(MapUtils.getRaw(map, 'titles') as SerializedData),
        context.fromData<LocalizedString>(MapUtils.getRaw(map, 'captions') as SerializedData),
        context.fromData<PhotoReference>(MapUtils.getRaw(map, 'photo') as SerializedData),
        MapUtils.getOrNull(map, 'url'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'titles': context.toData(this.titles),
      'captions': context.toData(this.captions),
      'photo': context.toDataOrNull(this.photo),
      'url': this.url,
    };
  }

  clone(): this {
    return new LegacyPointOfInterestItem(
        this.titles.clone(),
        this.captions.clone(),
        this.photo?.clone() ?? null,
        this.url,
    ) as this;
  }

  isSame(other: LegacyPointOfInterestItem): boolean {
    if(this === other) {
      return true;
    }

    return isSameData(other.titles, this.titles) &&
      isSameData(other.captions, this.captions) &&
      isSameData(other.photo, this.photo);
  }
}

export class LegacyPointOfInterestItemBuilder extends DataModelBuilder<LegacyPointOfInterestItem> {
  build(context: SerializationContext, map: SerializedData): LegacyPointOfInterestItem {
    return LegacyPointOfInterestItem.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(LegacyPointOfInterestItem.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(LegacyPointOfInterestItem.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version: number = versionRange.to;

    // In this version the [url] parameter was added.

    if(version < 2) {
      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
